/**
 * Excel日期值校正（精准版：区分Excel序列号Date和普通Date）
 * 解决"日期加1过度"问题，与后端逻辑完全对齐：
 * 1. Excel序列号解析的Date（XLSX.js默认返回）：补偿1900bug+时区
 * 2. 普通Date/字符串转Date：仅格式转换，不补偿
 * @param {Date|string} value - Excel解析的Date对象/日期字符串
 * @param {boolean} isExcelSerialDate - 是否是Excel序列号解析的Date（关键！）
 * @returns {string} 校正后的标准日期字符串（yyyy-MM-dd）
 */
export function fixExcelDate(value, isExcelSerialDate = false) {
  // 步骤1：统一转换为有效Date对象
  let dateObj;
  if (value instanceof Date) {
    // 入参是Date对象，校验有效性
    if (isNaN(value.getTime())) {
      console.warn('fixExcelDate：入参是无效Date对象，返回空字符串', value);
      return '';
    }
    dateObj = value;
  } else if (typeof value === 'string') {
    // 入参是字符串，尝试转换为Date
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return '';
    }
    // 支持yyyy/M/d、yyyy-MM-dd等格式转换
    dateObj = parseDateString(trimmedValue);
    if (!dateObj) {
      console.warn('fixExcelDate：字符串无法解析为有效日期', value);
      return '';
    }
  } else {
    console.warn('fixExcelDate：入参类型不支持', typeof value, value);
    return '';
  }

  try {
    let finalTime = dateObj.getTime();

    // 步骤2：仅对Excel序列号解析的Date补偿1900bug（核心！）
    if (isExcelSerialDate) {
      // 补偿Excel 1900闰年bug（少1天）
      finalTime += 86400000; // 1天=24*60*60*1000毫秒
      console.debug('fixExcelDate：执行Excel 1900bug补偿，加1天');
    }

    // 步骤3：东八区时区校正（仅针对时间戳，不额外加天）
    const gmt8Time = new Date(finalTime);
    // 基于UTC提取日期（避免本地时区干扰）
    const year = gmt8Time.getUTCFullYear();
    const month = String(gmt8Time.getUTCMonth() + 1).padStart(2, '0');
    const day = String(gmt8Time.getUTCDate()).padStart(2, '0');

    // 步骤4：生成标准格式
    const fixedDate = `${year}-${month}-${day}`;
    console.debug('Excel日期校正完成：', {
      originalValue: value instanceof Date ? value.toISOString() : value,
      isExcelSerialDate,
      fixedDate
    });
    return fixedDate;

  } catch (error) {
    console.error('Excel日期校正失败：', error, '原始值：', value);
    // 兜底：用ISO格式提取日期
    return dateObj.toISOString().split('T')[0];
  }
}

/**
 * 辅助方法：解析多种格式的日期字符串为Date对象
 * @param {string} str - 日期字符串（yyyy/M/d、yyyy-MM-dd、yyyy/MM/dd等）
 * @returns {Date|null} 解析后的Date对象，失败返回null
 */
function parseDateString(str) {
  // 匹配常见日期格式：yyyy/M/d、yyyy-MM-dd、yyyy/MM/dd
  const reg = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/;
  const match = str.match(reg);
  if (!match) {
    return null;
  }
  // 注意：月份-1（Date对象月份从0开始）
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);
  // 验证日期有效性（避免2026-02-30这类无效日期）
  const date = new Date(Date.UTC(year, month, day));
  if (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month &&
    date.getUTCDate() === day
  ) {
    return date;
  }
  return null;
}